import { isValidElement, type ReactNode } from "react";
import { cn, toSafeReactNode } from "@/lib/utils";

/** Stellt sicher, dass nur string, number oder ein gültiges React-Element gerendert wird – verhindert "Cannot convert object to primitive value". */
function safeCellContent(node: ReactNode): ReactNode {
  if (node === null || node === undefined) return "";
  if (typeof node === "string" || typeof node === "number" || typeof node === "boolean") return node;
  if (isValidElement(node)) return node;
  return toSafeReactNode(node);
}

export interface DataTableColumn<T> {
  id: string;
  header: string;
  cell: (row: T) => ReactNode;
  className?: string;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  keyFn: (row: T) => string;
  loading?: boolean;
  emptyMessage?: string;
  className?: string;
}

export function DataTable<T>({
  columns,
  data,
  keyFn,
  loading = false,
  emptyMessage = "Keine Einträge",
  className,
}: DataTableProps<T>) {
  return (
    <div className={cn("overflow-x-auto rounded-lg border border-border", className)}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-surface-2">
            {columns.map((col) => (
              <th
                key={col.id}
                className={cn(
                  "px-4 py-3 text-left font-medium text-text",
                  col.className
                )}
              >
                {toSafeReactNode(col.header as unknown)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-surface">
          {loading ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-8 text-center text-text-muted"
              >
                Wird geladen…
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-8 text-center text-text-muted"
              >
                {toSafeReactNode(emptyMessage)}
              </td>
            </tr>
          ) : (
            data.map((row) => (
              <tr
                key={keyFn(row)}
                className="border-b border-border last:border-b-0 hover:bg-surface-2/50"
              >
                {columns.map((col) => (
                  <td
                    key={col.id}
                    className={cn("px-4 py-3 text-text", col.className)}
                  >
                    {safeCellContent(col.cell(row))}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
